/**
 * GTM Acquisition Attribution & UTM Parameter Tracker
 * Automatically captures, sanitizes, and persists UTM parameters and referral codes
 * across user sessions for investor metrics attribution.
 */

export interface AcquisitionParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  ref_code?: string;
  campus_code?: string;
  captured_at?: string;
}

const STORAGE_KEY = 'connectx_acquisition_params';
const MAX_PARAM_LENGTH = 64;

function sanitizeParam(val: string | null): string | undefined {
  if (!val) return undefined;
  const trimmed = val.trim().replace(/[^a-zA-Z0-9_.-]/g, '');
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_PARAM_LENGTH);
}

/**
 * Capture acquisition parameters from a query string or current window location.
 */
export function captureAcquisitionParams(queryString?: string): AcquisitionParams {
  if (typeof window === 'undefined') return {};

  try {
    const query = queryString ?? window.location.search;
    if (!query) return getAcquisitionParams();

    const params = new URLSearchParams(query);
    const source = sanitizeParam(params.get('utm_source'));
    const medium = sanitizeParam(params.get('utm_medium'));
    const campaign = sanitizeParam(params.get('utm_campaign'));
    const refCode = sanitizeParam(params.get('ref_code') ?? params.get('ref') ?? params.get('referral'));
    const campusCode = sanitizeParam(params.get('campus_code') ?? params.get('campus'));

    // If no acquisition parameters exist in the current URL, return stored parameters
    if (!source && !medium && !campaign && !refCode && !campusCode) {
      return getAcquisitionParams();
    }

    const existing = getAcquisitionParams();

    const updated: AcquisitionParams = {
      ...existing,
      ...(source ? { utm_source: source } : {}),
      ...(medium ? { utm_medium: medium } : {}),
      ...(campaign ? { utm_campaign: campaign } : {}),
      ...(refCode ? { ref_code: refCode } : {}),
      ...(campusCode ? { campus_code: campusCode } : {}),
      captured_at: existing.captured_at ?? new Date().toISOString(),
    };

    const json = JSON.stringify(updated);
    try {
      sessionStorage.setItem(STORAGE_KEY, json);
      localStorage.setItem(STORAGE_KEY, json);
    } catch {
      /* ignore storage quota/privacy errors */
    }

    return updated;
  } catch {
    return getAcquisitionParams();
  }
}

/**
 * Get stored acquisition parameters.
 */
export function getAcquisitionParams(): AcquisitionParams {
  if (typeof window === 'undefined') return {};

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as AcquisitionParams;
  } catch {
    return {};
  }
}

/**
 * Clear stored acquisition parameters (e.g. after successful signup attribution).
 */
export function clearAcquisitionParams(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// Auto-run capture on script execution if in browser environment
if (typeof window !== 'undefined') {
  captureAcquisitionParams();
}
